import { i18nNsFactory } from '@atlas-frontend/i18n';
import { path } from '../../paths';
import { withParams } from '../../paths/withParams';
import translations from './ErrorLevel.translations.json';
import { FC, memo, ReactNode, useMemo } from 'react';
import {
  getValidationErrorLevelColor,
  TableOperationButton,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import * as Icon from 'react-feather';
import {
  colorAccent1,
  colorInfo,
  fontSizeSM,
} from '@atlas-frontend/design-tokens';
import { ErrorSegment, ErrorReportType } from './types';

const { addTranslations, t } = i18nNsFactory('output-data-error-levels');
addTranslations(translations);

interface ErrorLevelProps {
  type: ErrorReportType;
  typeId: number;
  outputDataObjectId?: number | null;
  outputProcessingDataObjectId?: number | null;
  calculationErrors: boolean;
  missingSourceData: boolean;
  versionValidationErrorLevelId: number | null;
  validationErrorLevels: Map<number, { code: string }>;
  /*
    In "full" mode (default) all indicators (icons) for different error types (calculation, missing source data, validation) are rendered independently.
    In "compact" mode, other indicators are hidden, if there is a calculation error.
  */
  mode?: 'full' | 'compact';
  hasAccess?: boolean;
}

export const ErrorLevel: FC<ErrorLevelProps> = memo((props) => {
  const {
    type,
    typeId,
    outputDataObjectId,
    outputProcessingDataObjectId,
    calculationErrors,
    missingSourceData,
    versionValidationErrorLevelId,
    validationErrorLevels,
    mode = 'full',
    hasAccess,
  } = props;

  const opacity = hasAccess ? 1 : 0.6;
  const validationErrorLevel =
    versionValidationErrorLevelId !== null
      ? (validationErrorLevels.get(versionValidationErrorLevelId)?.code ?? null)
      : null;

  const errorButtons = useMemo(() => {
    const compact = mode === 'compact';

    const buttonConfigs = [
      {
        id: 'calculation-error-level-button',
        render: () => {
          if (!compact || calculationErrors) return true;
          if (!!missingSourceData && !validationErrorLevel) return false;

          return !missingSourceData && !!validationErrorLevel;
        },
        navigable: true,
        visible: calculationErrors,
        icon: (
          <Icon.AlertTriangle
            size={18}
            color={colorAccent1}
            opacity={opacity}
          />
        ),
        errorSegment: 'calculationErrors',
      },
      {
        id: 'missing-source-error-level-button',
        render: () => {
          if (!compact) return true;
          if (calculationErrors) return false;
          return !!missingSourceData;
        },
        navigable: true,
        visible: (mode === 'full' || !calculationErrors) && missingSourceData,
        icon: (
          <Icon.AlertTriangle size={18} color={colorInfo} opacity={opacity} />
        ),
        errorSegment: 'missingSourceData',
      },
      {
        id: 'validation-error-level-button',
        render: () => {
          if (!compact) return true;
          if (calculationErrors) return false;
          return !!validationErrorLevel;
        },
        navigable:
          validationErrorLevel !== 'NO_RULES' &&
          validationErrorLevel !== 'SUCCESS',
        visible:
          (mode === 'full' || !calculationErrors) && !!validationErrorLevel,
        icon: (
          <span
            style={{
              fontSize: fontSizeSM,
              color: getValidationErrorLevelColor(validationErrorLevel),
              opacity,
            }}
          >
            VR
          </span>
        ),
        errorSegment: 'validationErrors',
      },
    ];

    return buttonConfigs;
  }, [
    mode,
    calculationErrors,
    opacity,
    missingSourceData,
    validationErrorLevel,
  ]);

  return (
    <Flex gap="small">
      {errorButtons
        .filter(({ render }) => render())
        .map(({ id, errorSegment, visible, icon, navigable }) => (
          <ErrorButton
            key={id}
            id={id}
            href={withParams(path.outputDataErrorReport, {
              id: typeId,
              type,
              descriptionId: outputDataObjectId || undefined,
              processingDataObjectId: outputProcessingDataObjectId || undefined,
              errorSegment: errorSegment as ErrorSegment,
            })}
            hasAccess={hasAccess}
            visible={visible}
            navigable={navigable}
            icon={icon}
          />
        ))}
    </Flex>
  );
});

export const ErrorButton: FC<{
  id: string;
  href?: string;
  icon: ReactNode;
  hasAccess?: boolean;
  visible?: boolean;
  navigable?: boolean;
}> = ({ id, href, icon, hasAccess, visible = true, navigable }) => {
  const enabled = visible && hasAccess && navigable && !!href;

  let title;
  if (!visible) {
    title = undefined;
  } else if (!hasAccess) {
    title = t('noAccess');
  } else if (navigable) {
    title = t('openReport');
  }

  return (
    <TableOperationButton
      title={title}
      id={id}
      type="default"
      icon={icon}
      disabled={!enabled}
      href={enabled ? href : undefined}
      target="_blank"
      rel="opener"
      style={{
        visibility: visible ? 'visible' : 'hidden',
      }}
    />
  );
};
